import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  BottomNavbarComponent,
  GameDetailsComponent,
  OrAvatarComponent,
  OrIconComponent,
  PageLayoutComponent,
  TitileNavbarComponent
} from '@ui-components';
import { shareReplay, switchMap, tap } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  CacheKey,
  GameDetailsModel,
  GamePlayerModel,
  LocalRefreshService,
  MODAL_TOKEN,
  ModalProvider,
  provideLocalRefreshService,
  RedirectToUserProfileDirective,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import { ManageMyGameComponent } from '../manage-my-game/manage-my-game.component';
import { OpenSingleChatCommandHandler } from '@messages';
import { GameDetailsQueryHandler } from '../../application/handlers';
import { ChatService } from '@messages/infra';
import { UserParticipationStatus } from '@core-http';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'lib-smy-game-details',
  templateUrl: './my-game-details.component.html',
  styleUrls: ['./my-game-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TitileNavbarComponent,
    BottomNavbarComponent,
    GameDetailsComponent,
    OrAvatarComponent,
    RedirectToUserProfileDirective,
    PageLayoutComponent,
    NgTemplateOutlet,
    OrIconComponent
  ],
  providers: [provideLocalRefreshService(), OpenSingleChatCommandHandler, ChatService]
})
export class MyGameDetailsComponent {
  private readonly gameDetailsQueryHandler: GameDetailsQueryHandler = inject(GameDetailsQueryHandler);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly localRefreshService: LocalRefreshService = inject(LocalRefreshService);
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly openSingleChatCommandHandler: OpenSingleChatCommandHandler = inject(OpenSingleChatCommandHandler);
  private readonly router: Router = inject(Router);

  readonly gameDetails: Signal<GameDetailsModel | undefined> = toSignal(
    this.modalProvider.showLoading$().pipe(
      switchMap(() => this.localRefreshService.refresh$),
      switchMap(() =>
        this.activatedRoute.params.pipe(
          switchMap((params: Params) => this.gameDetailsQueryHandler.getGameDetails(params['id']))
        )
      ),
      tap(() => this.modalProvider.dismissLoading$()),
      shareReplay(1)
    )
  );

  private readonly userData: Signal<UserModel | undefined> = toSignal(this.userDataProvider.userData$);

  readonly showPhoneNumber: Signal<boolean> = computed(() => {
    const game: GameDetailsModel | undefined = this.gameDetails();
    const userData: UserModel | undefined = this.userData();
    if (!game || !userData) return false;
    return !!game.players?.some((player: GamePlayerModel) => player?.userId === userData.userId);
  });

  shouldHideMessageIcon(userId: string): boolean {
    return this.userData()?.userId === userId;
  }

  manageGame(): void {
    const game: GameDetailsModel | undefined = this.gameDetails();
    if (!game) return;

    this.modalProvider
      .showModal$({
        component: ManageMyGameComponent,
        componentProps: { game },
        cssClass: 'modal-auto',
        initialBreakpoint: 1,
        breakpoints: [0, 1]
      })
      .subscribe(() => this.localRefreshService.emit());
  }

  sendMessage(payload: { userId: string; displayName: string | undefined }): void {
    const redirectToGameFromChatUrl: string = this.router.url;
    sessionStorage.setItem(CacheKey.RedirectToGameFromChatUrl, redirectToGameFromChatUrl);
    this.openSingleChatCommandHandler.open(payload.userId, payload.displayName).subscribe();
  }

  showGoToChat(gameStatus: UserParticipationStatus | undefined): boolean {
    return gameStatus === UserParticipationStatus.Approved || gameStatus === UserParticipationStatus.Host;
  }
}
